
import { supabase } from '@/integrations/supabase/client';
import { AgentCapability } from '@/types/agentic';
import { toast } from 'sonner';

// Export AgentCapability type for reuse
export type { AgentCapability } from '@/types/agentic';

/**
 * Fetch all capabilities
 */
export const fetchCapabilities = async (): Promise<AgentCapability[]> => {
  try {
    const { data, error } = await supabase
      .from('agent_capabilities')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching capabilities:', error);
      toast.error('Failed to fetch capabilities');
      throw error;
    }

    return data as AgentCapability[];
  } catch (error) {
    console.error('Error in fetchCapabilities:', error);
    toast.error('Failed to fetch capabilities');
    throw error;
  }
};

/**
 * Fetch a capability by ID
 */
export const fetchCapabilityById = async (id: string): Promise<AgentCapability | null> => {
  try {
    const { data, error } = await supabase
      .from('agent_capabilities')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching capability with ID ${id}:`, error);
      toast.error('Failed to fetch capability');
      throw error;
    }

    return data as AgentCapability;
  } catch (error) {
    console.error('Error in fetchCapabilityById:', error);
    toast.error('Failed to fetch capability');
    throw error;
  }
};

/**
 * Create a new capability
 */
export const createCapability = async (capability: Omit<AgentCapability, 'id' | 'created_at' | 'updated_at'>): Promise<AgentCapability> => {
  try {
    const { data, error } = await supabase
      .from('agent_capabilities')
      .insert(capability)
      .select()
      .single();

    if (error) {
      console.error('Error creating capability:', error);
      toast.error('Failed to create capability');
      throw error;
    }

    toast.success('Capability created successfully');
    return data as AgentCapability;
  } catch (error) {
    console.error('Error in createCapability:', error);
    toast.error('Failed to create capability');
    throw error;
  }
};

/**
 * Update an existing capability
 */
export const updateCapability = async (id: string, capability: Partial<AgentCapability>): Promise<AgentCapability> => {
  try {
    const { data, error } = await supabase
      .from('agent_capabilities')
      .update(capability)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating capability with ID ${id}:`, error);
      toast.error('Failed to update capability');
      throw error;
    }

    toast.success('Capability updated successfully');
    return data as AgentCapability;
  } catch (error) {
    console.error('Error in updateCapability:', error);
    toast.error('Failed to update capability');
    throw error;
  }
};

/**
 * Delete a capability
 */
export const deleteCapability = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('agent_capabilities')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error deleting capability with ID ${id}:`, error);
      toast.error('Failed to delete capability');
      throw error;
    }

    toast.success('Capability deleted successfully');
  } catch (error) {
    console.error('Error in deleteCapability:', error);
    toast.error('Failed to delete capability');
    throw error;
  }
};
